import styled from 'styled-components';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import React, { FC, useContext, useState } from 'react';
import { IAnalysisReport } from '../../views/Monitoring/Analysis/Analysis';
import { constants } from '../../constants/constants';
import Accordion from '../Accordion/Accordion';
import StarRating from '../StarRating/StarRating';
import NumberUtils from '../../shared/NumberUtils';
import { Close, Comment, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Helper from '../../shared/helpers/Helper';
import ReviewAiService from '../../services/ReviewAiService';
import Confirm from '../Confirm/Confirm';
import { AuthContext } from '../../providers/AuthProvider';

import './aireview.css';
import AnalysisReportContent from './AnalysisReportContent';
import PlatformImage from '../Logo/PlatformImage';

export const AccordionSummaryContentBox = styled(Box)`
  display: flex;
  align-items: center;
`;

interface Props {
  report: IAnalysisReport;
  onDelete: () => void;
}

const AnalysisReport: FC<Props> = ({ report, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState<boolean>(false);
  const [purgeIsOpen, setPurgeIsOpen] = useState<boolean>(false);

  const { hasRole } = useContext(AuthContext);

  const theme = useTheme();
  const { t } = useTranslation();
  const platformName: string = report.platform;

  async function handleDelete() {
    const success = await ReviewAiService.deleteById(report.id);
    if (success) {
      onDelete();
    }
  }

  async function handlePurge() {
    const success = await ReviewAiService.purgeById(report.id);
    if (success) {
      onDelete();
    }
  }

  return (
    <>
      <Accordion
        sx={{ opacity: report.completedAt != null ? 1 : 0.7 }}
        expanded={report.completedAt != null && isExpanded}
        onExpandRequest={() => setIsExpanded((prevState) => !prevState)}
        summary={
          <>
            <AccordionSummaryContentBox
              sx={{
                flex: '1 1 10%',
              }}
            >
              {<PlatformImage src={constants.platform_logos[platformName]} />}
            </AccordionSummaryContentBox>

            <AccordionSummaryContentBox
              sx={{
                flex: '1 1 7.5%',
              }}
            >
              <Typography
                variant={'body2'}
                fontSize={'0.7em'}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {Helper.getFormattedDate(report.dateFrom, true) || t('all')} -{' '}
                {Helper.getFormattedDate(report.dateTo, true) ||
                  Helper.getFormattedDate(report.createdAt, true)}
              </Typography>
            </AccordionSummaryContentBox>
            <AccordionSummaryContentBox
              sx={{
                flex: '1 1 20%',
                overflow: 'hidden',
                mx: theme.spacing(2),
                whiteSpace: 'nowrap',
              }}
            >
              <Tooltip
                title={report.title || report.company}
                placement="bottom-start"
                arrow
              >
                <Typography
                  variant="body1"
                  sx={{
                    cursor: 'pointer',
                    color: theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main,
                  }}
                >
                  {report.title || report.company}
                </Typography>
              </Tooltip>
            </AccordionSummaryContentBox>
            {report.completedAt != null ? (
              <>
                <Tooltip
                  arrow
                  title={t('analysis_total_score')}
                >
                  <AccordionSummaryContentBox sx={{ flex: '1 1 35%' }}>
                    <StarRating value={report.rating}></StarRating>
                    <Typography
                      variant="body2"
                      sx={{
                        ml: theme.spacing(1),
                        color: theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main,
                      }}
                    >
                      {NumberUtils.formatScore(report.rating)} {t('of')} 5 {t('stars')}
                    </Typography>
                  </AccordionSummaryContentBox>
                </Tooltip>

                <Tooltip
                  arrow
                  title={t('num_analysed_reports')}
                >
                  <AccordionSummaryContentBox sx={{ flex: '2 1 25%' }}>
                    <Comment fontSize="small" />
                    <Typography
                      variant="body2"
                      sx={{ ml: theme.spacing(1) }}
                    >
                      {report.analyzedCount}{' '}
                      {report.reviewCount > 1 ? `${t('reviews')}` : `${t('review')}`}
                    </Typography>
                  </AccordionSummaryContentBox>
                </Tooltip>
              </>
            ) : (
              <>
                <AccordionSummaryContentBox sx={{ flex: '1 1 35%' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      ml: theme.spacing(1),
                      color: theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main,
                    }}
                  >
                    {t('creating_analysis')}
                  </Typography>
                </AccordionSummaryContentBox>

                <AccordionSummaryContentBox sx={{ flex: '2 1 25%' }}></AccordionSummaryContentBox>
              </>
            )}
            <AccordionSummaryContentBox
              sx={{ flex: '2 1 25%', marginLeft: 'auto', justifyContent: 'flex-end' }}
            >
              <Typography
                variant="body2"
                sx={{ ml: theme.spacing(1) }}
              >
                {Helper.getFormattedDate(report.createdAt, true)}
              </Typography>
              <Box
                sx={{ display: 'flex', ml: theme.spacing(2), cursor: 'pointer' }}
                onClick={() => setDeleteIsOpen(true)}
              >
                <Tooltip
                  title={t('delete')}
                  placement="top"
                  arrow
                >
                  <Delete
                    fontSize="small"
                    sx={{ color: theme.palette.error.main }}
                  />
                </Tooltip>
              </Box>
              {hasRole('can-purge-aireviews') && (
                <Box
                  sx={{ display: 'flex', ml: theme.spacing(2), cursor: 'pointer' }}
                  onClick={() => setPurgeIsOpen(true)}
                >
                  <Tooltip
                    title={t('purge')}
                    placement="top"
                    arrow
                  >
                    <Close
                      fontSize="small"
                      sx={{ color: theme.palette.error.main }}
                    />
                  </Tooltip>
                </Box>
              )}
            </AccordionSummaryContentBox>
          </>
        }
        details={
          <>
            <AnalysisReportContent reportData={report.result} />
          </>
        }
      ></Accordion>
      <Confirm
        text={t('delete_confirm')}
        open={deleteIsOpen}
        onCancel={() => setDeleteIsOpen(false)}
        onOk={async () => await handleDelete()}
      />
      <Confirm
        text={t('delete_confirm')}
        open={purgeIsOpen}
        onCancel={() => setPurgeIsOpen(false)}
        onOk={async () => await handlePurge()}
      />
    </>
  );
};

export default AnalysisReport;
